// Org Section — pillar share + top BUs + designation pyramid.

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownRow,
} from '../../types/hrStats';
import { usePeopleBreakdown } from '../../hooks/usePeopleBreakdown';
import { formatNumberFull } from '../../utils/FormatUtils';
import SectionCard from './primitives/SectionCard';
import HBarRow from './primitives/HBarRow';
import { ACCENTS, colorByIndex } from './primitives/peopleInsightsTokens';

export interface OrgSectionProps {
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  onApplyFilter: (dim: HrDimension, value: string) => void;
}

const TOP_BU_LIMIT = 8;

const OrgSection: React.FC<OrgSectionProps> = ({
  pageFilters,
  employeeStatus,
  onApplyFilter,
}) => {
  const pillarsQuery = usePeopleBreakdown({
    dimension: 'pillar',
    filters: pageFilters,
    employeeStatus,
  });
  const busQuery = usePeopleBreakdown({
    dimension: 'business_unit',
    filters: pageFilters,
    employeeStatus,
  });
  const designationsQuery = usePeopleBreakdown({
    dimension: 'designation',
    filters: pageFilters,
    employeeStatus,
  });

  // Compute decorated pillars in a single useMemo whose only dep is the
  // query data itself — avoids the `?? []` re-creating a new array on each
  // render and forcing the memo to re-run.
  //
  // Colour is assigned by rank (largest pillar first → palette[0], etc.) so
  // adjacent stacked-bar segments are always visually distinct. This avoids
  // the previous hash-based assignment, which collided regularly because
  // pillar labels share common words ("Deliver", "Enable", "Sell").
  const pillars = useMemo(() => {
    const rows = pillarsQuery.data?.rows ?? [];
    return rows.map((row, idx) => ({ ...row, color: colorByIndex(idx) }));
  }, [pillarsQuery.data]);
  const pillarTotal = pillarsQuery.data?.total ?? 0;

  const topBus = (busQuery.data?.rows ?? []).slice(0, TOP_BU_LIMIT);
  const designations = designationsQuery.data?.rows ?? [];
  const designationMax =
    designations.length > 0 ? Math.max(...designations.map((d) => d.count)) : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 2,
      }}
    >
      {/* LEFT: pillars + top BUs */}
      <SectionCard
        title="Pillars & Business Units"
        subtitle="Click a pillar segment or BU to drill in."
        icon={
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              backgroundColor: ACCENTS.indigo.surface,
              color: ACCENTS.indigo.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BusinessIcon fontSize="small" />
          </Box>
        }
      >
        {/* Pillar stacked bar */}
        {pillarsQuery.isLoading ? (
          <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
        ) : pillarsQuery.isError ? (
          <Alert severity="error">Failed to load pillar breakdown.</Alert>
        ) : pillars.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No pillar data.
          </Typography>
        ) : (
          <>
            <Stack
              direction="row"
              sx={{
                height: 44,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                mb: 1.5,
              }}
            >
              {pillars.map((p) => {
                const widthPct = pillarTotal > 0 ? (p.count / pillarTotal) * 100 : 0;
                if (widthPct <= 0) return null;
                return (
                  <Tooltip
                    key={p.key}
                    title={`${p.label}: ${formatNumberFull(p.count)} (${p.percent.toFixed(1)}%)`}
                    placement="top"
                  >
                    <ButtonBase
                      onClick={() => onApplyFilter('pillar', p.key)}
                      sx={{
                        flexBasis: `${widthPct}%`,
                        flexGrow: 0,
                        flexShrink: 0,
                        backgroundColor: p.color,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        transition: 'opacity 120ms ease',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      {p.percent >= 4 && `${p.percent.toFixed(0)}%`}
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Stack>
            <Stack direction="row" flexWrap="wrap" rowGap={1} columnGap={2} sx={{ mb: 2 }}>
              {pillars.map((p) => (
                <Stack key={p.key} direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 0.5,
                      backgroundColor: p.color,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {p.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {formatNumberFull(p.count)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {/* Top BUs */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 1,
            mt: 1,
          }}
        >
          Top Business Units
        </Typography>
        {busQuery.isLoading ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={44} sx={{ my: 0.5 }} />
          ))
        ) : busQuery.isError ? (
          <Alert severity="error">Failed to load BU breakdown.</Alert>
        ) : topBus.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No business units to display.
          </Typography>
        ) : (
          topBus.map((bu, idx) => {
            const color = colorByIndex(idx);
            return (
              <HBarRow
                key={bu.key}
                label={bu.label}
                sublabel={`${bu.percent.toFixed(1)}% of selection`}
                count={bu.count}
                maxCount={topBus[0]?.count ?? 0}
                color={color}
                accentStripeColor={color}
                onClick={() => onApplyFilter('business_unit', bu.key)}
              />
            );
          })
        )}
      </SectionCard>

      {/* RIGHT: designation pyramid */}
      <SectionCard
        title="Designation Pyramid"
        subtitle="Distribution of seniority across active workforce."
        icon={
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              backgroundColor: ACCENTS.amber.surface,
              color: ACCENTS.amber.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmojiEventsIcon fontSize="small" />
          </Box>
        }
      >
        {designationsQuery.isLoading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={32} sx={{ my: 0.5 }} />
          ))
        ) : designationsQuery.isError ? (
          <Alert severity="error">Failed to load designations.</Alert>
        ) : designations.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No designation data.
          </Typography>
        ) : (
          <Stack
            spacing={0.75}
            sx={{
              // Cap the height so the pyramid stays compact when the
              // backend returns dozens of designations; scroll for the rest.
              maxHeight: 420,
              overflowY: 'auto',
              pr: 0.5,
              // Subtle scroll-shadow so users notice there's more content.
              backgroundImage: `
                linear-gradient(to top, rgba(15, 23, 42, 0.04), transparent 12px),
                linear-gradient(to bottom, rgba(15, 23, 42, 0.04), transparent 12px)
              `,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 12px, 100% 12px',
              backgroundPosition: 'bottom, top',
            }}
          >
            {designations.map((d: HrStatsBreakdownRow) => {
              const pct = designationMax > 0 ? (d.count / designationMax) * 100 : 0;
              return (
                <ButtonBase
                  key={d.key}
                  onClick={() => onApplyFilter('designation', d.key)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr',
                    alignItems: 'center',
                    columnGap: 1,
                    px: 0.5,
                    py: 0.5,
                    borderRadius: 1,
                    width: '100%',
                    '&:hover': { backgroundColor: '#F8FAFC' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'right',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {d.label}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 26,
                      backgroundColor: '#F8FAFC',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${(100 - pct) / 2}%`,
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${ACCENTS.indigo.main}, ${ACCENTS.violet.main})`,
                        borderRadius: 1,
                      }}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {formatNumberFull(d.count)}
                    </Stack>
                  </Box>
                </ButtonBase>
              );
            })}
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
};

export default OrgSection;
